function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
import { broadcastToDocument } from "./events";
import { updateStorage } from "./storage-utils";
/**
 * Removes a value from the storage.
 * @param {string} name - The name of the value to get.
 * @param {boolean} broadcast - Whether to broadcast the change.
 * @returns {void}
 */ export var remove = function(name, broadcast) {
    if (broadcast === void 0) broadcast = true;
    updateStorage(sessionStorage, name, null);
    updateStorage(localStorage, name, null);
    if (broadcast) {
        broadcastToDocument("" + name + "Set");
    }
};
/**
 * Checks if a value exists in the storage.
 * @param {string} name - The name of the value to get.
 * @returns {boolean}
 */ export var has = function(name) {
    var item = get(name);
    return item !== null;
};
/**
 * Sets a value in the storage.
 * @param {string} name - The name of the value to get.
 * @param {value} value - The value to set.
 * @param {object} options - The options to set.
 * @param {number} options.expires - The number of days to expire the value.
 * @param {boolean} broadcast - Whether to broadcast the change.
 * @returns {void}
 */ export var set = function(name, value, options, broadcast) {
    if (options === void 0) options = {};
    if (broadcast === void 0) broadcast = true;
    var shouldBroadcast = broadcast;
    var item = {
        value: value
    };
    if (value === null || value === undefined) {
        remove(name, shouldBroadcast);
        return;
    }
    // If the value is the same as the current value, don't broadcast.
    if (get(name, false) === value) {
        shouldBroadcast = false;
    }
    var storage = sessionStorage;
    if (options.hasOwnProperty("expires")) {
        storage = localStorage;
        var expires = options.expires;
        if (typeof expires === "number") {
            // expires is assumed to be in days
            // 864e5 is the number of milliseconds in a day (1000 * 60 * 60 * 24)
            expires = new Date(Date.now() + expires * 864e5);
        } else if (typeof expires === "string") {
            expires = new Date(expires);
        }
        // If the expires value is a valid date, set it in local storage.
        if (expires instanceof Date) {
            storage = localStorage;
            item.expires = expires.toUTCString();
        }
    }
    // Could be in session or local storage, so remove from both to ensure it's not.
    remove(name, false);
    updateStorage(storage, name, JSON.stringify(item));
    if (shouldBroadcast) {
        broadcastToDocument("" + name + "Set");
    }
};
/**
 * Get a value from storage.
 * @param {string} name - The name of the item to get
 * @param {boolean} broadcast - Whether to broadcast the get event
 * @returns {any} - The value of the item with the given name
 */ export var get = function(name, broadcast) {
    if (broadcast === void 0) broadcast = true;
    var itemString = sessionStorage.getItem(name);
    if (!itemString) {
        itemString = localStorage.getItem(name);
    }
    if (!itemString) {
        return null;
    }
    try {
        var item = JSON.parse(itemString);
        if (item.hasOwnProperty("expires") && Date.parse(item.expires) < Date.now()) {
            // Only local storage items have an expiration date
            updateStorage(localStorage, name, null);
            if (broadcast) {
                broadcastToDocument("" + name + "Set");
            }
            return null;
        }
        return item.value;
    } catch (err) {
        // Any error handling the parsed JSON object results in returning the raw string
        return itemString;
    }
};
/**
 * Perform storage operations on a set of items.
 * @param {object} set - The items to set.
 * @param {object} remove - The items to remove.
 * @param {object} get - The items to get.
 * @returns {object} - The values of the items that were retrieved.
 * @example
 * handleStorage({
 *   set: [
 *     {
 *       name: "foo",
 *       value: "bar",
 *       options: {
 *         expires: 1,
 *       },
 *     },
 *   ],
 *   remove: [
 *     {
 *       name: "something",
 *     },
 *   ],
 *  get: ["foo", "something"],
 * }); // Returns: { foo: "bar", something: null }
 */ export var handleStorage = function(param) {
    var tmp = param.set, s = tmp === void 0 ? [] : tmp, tmp1 = param.remove, r = tmp1 === void 0 ? [] : tmp1, tmp2 = param.get, g = tmp2 === void 0 ? [] : tmp2;
    s.forEach(function(param) {
        var name = param.name, value = param.value, _param_options = param.options, options = _param_options === void 0 ? {} : _param_options;
        var preventOverwrite = options.preventOverwrite;
        // Provides ability to create `sticky` cookies that will only set once, when the cookie does not exist
        if (preventOverwrite && get(name) !== undefined) {
            return;
        }
        set(name, value, options);
    });
    r.forEach(function(param) {
        var name = param.name;
        remove(name);
    });
    return g.reduce(function(previous, name) {
        return _extends({}, previous, _define_property({}, name, get(name)));
    }, {});
};
// Create a storage object that can be used to set, get, and remove items from storage
export default Object.create({
    set: set,
    get: get,
    has: has,
    remove: remove
});

//# sourceMappingURL=storage.js.map